import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { apiService } from '../../services/api'
import type { Form, PaginationParams, SearchFilters } from '../../types'

interface FormState {
  forms: Form[]
  currentForm: Form | null
  loading: boolean
  error: string | null
  pagination: {
    page: number
    limit: number
    total: number
    totalPages: number
  }
  filters: SearchFilters
}

const initialState: FormState = {
  forms: [],
  currentForm: null,
  loading: false,
  error: null,
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  },
  filters: {},
}

export const fetchForms = createAsyncThunk(
  'form/fetchForms',
  async ({ filters, pagination }: { filters?: SearchFilters; pagination?: PaginationParams }) => {
    const response = await apiService.getForms(filters, pagination)
    if (response.error) {
      throw new Error(response.error)
    }
    return response.data
  }
)

export const fetchFormById = createAsyncThunk(
  'form/fetchFormById',
  async (id: string | number) => {
    const response = await apiService.getForm(id)
    if (response.error) {
      throw new Error(response.error)
    }
    return response.data?.form
  }
)

export const createForm = createAsyncThunk(
  'form/createForm',
  async (formData: Partial<Form>) => {
    const response = await apiService.createForm(formData)
    if (response.error) {
      throw new Error(response.error)
    }
    return response.data?.form
  }
)

export const updateForm = createAsyncThunk(
  'form/updateForm',
  async ({ id, formData }: { id: string | number; formData: Partial<Form> }) => {
    const response = await apiService.updateForm(id, formData)
    if (response.error) {
      throw new Error(response.error)
    }
    return response.data?.form
  }
)

export const deleteForm = createAsyncThunk(
  'form/deleteForm',
  async (id: string | number) => {
    const response = await apiService.deleteForm(id)
    if (response.error) {
      throw new Error(response.error)
    }
    return id
  }
)

export const publishForm = createAsyncThunk(
  'form/publishForm',
  async (id: string | number) => {
    const response = await apiService.publishForm(id)
    if (response.error) {
      throw new Error(response.error)
    }
    return response.data?.form
  }
)

export const unpublishForm = createAsyncThunk(
  'form/unpublishForm',
  async (id: string | number) => {
    const response = await apiService.unpublishForm(id)
    if (response.error) {
      throw new Error(response.error)
    }
    return response.data?.form
  }
)

const formSlice = createSlice({
  name: 'form',
  initialState,
  reducers: {
    setCurrentForm: (state, action: PayloadAction<Form | null>) => {
      state.currentForm = action.payload
    },
    setFilters: (state, action: PayloadAction<SearchFilters>) => {
      state.filters = action.payload
    },
    setPagination: (state, action: PayloadAction<Partial<FormState['pagination']>>) => {
      state.pagination = { ...state.pagination, ...action.payload }
    },
    clearError: (state) => {
      state.error = null
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch forms
      .addCase(fetchForms.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchForms.fulfilled, (state, action) => {
        state.loading = false
        state.forms = action.payload?.data || []
        state.pagination = {
          page: action.payload?.meta?.page || 1,
          limit: action.payload?.meta?.limit || 10,
          total: action.payload?.meta?.total || 0,
          totalPages: action.payload?.meta?.totalPages || 0,
        }
      })
      .addCase(fetchForms.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to fetch forms'
      })
      // Fetch form by id
      .addCase(fetchFormById.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchFormById.fulfilled, (state, action) => {
        state.loading = false
        state.currentForm = action.payload || null
      })
      .addCase(fetchFormById.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to fetch form'
      })
      // Create form
      .addCase(createForm.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(createForm.fulfilled, (state, action) => {
        state.loading = false
        if (action.payload) {
          state.forms.unshift(action.payload)
        }
      })
      .addCase(createForm.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to create form'
      })
      // Update form
      .addCase(updateForm.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updateForm.fulfilled, (state, action) => {
        state.loading = false
        if (action.payload) {
          const payload = action.payload
          const index = state.forms.findIndex((f) => f.id === payload.id)
          if (index !== -1) {
            state.forms[index] = payload
          }
          if (state.currentForm?.id === payload.id) {
            state.currentForm = payload
          }
        }
      })
      .addCase(updateForm.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to update form'
      })
      // Delete form
      .addCase(deleteForm.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(deleteForm.fulfilled, (state, action) => {
        state.loading = false
        state.forms = state.forms.filter((f) => f.id !== action.payload)
        if (state.currentForm?.id === action.payload) {
          state.currentForm = null
        }
      })
      .addCase(deleteForm.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to delete form'
      })
      // Publish form
      .addCase(publishForm.fulfilled, (state, action) => {
        if (action.payload) {
          const payload = action.payload
          const index = state.forms.findIndex((f) => f.id === payload.id)
          if (index !== -1) {
            state.forms[index] = payload
          }
          if (state.currentForm?.id === payload.id) {
            state.currentForm = payload
          }
        }
      })
      // Unpublish form
      .addCase(unpublishForm.fulfilled, (state, action) => {
        if (action.payload) {
          const payload = action.payload
          const index = state.forms.findIndex((f) => f.id === payload.id)
          if (index !== -1) {
            state.forms[index] = payload
          }
          if (state.currentForm?.id === payload.id) {
            state.currentForm = payload
          }
        }
      })
  },
})

export const { setCurrentForm, setFilters, setPagination, clearError } = formSlice.actions
export default formSlice.reducer
